"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import type { Certificate } from "@/types/database";

type Props = {
    certificates: Certificate[];
};

export function CertificateGallery({ certificates }: Props) {
    const [selected, setSelected] = useState<Certificate | null>(null);

    return (
        <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {certificates.map((cert) => (
                    <motion.button
                        key={cert.id}
                        whileHover={{ y: -4 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => cert.image_url ? setSelected(cert) : undefined}
                        className="clay-card group overflow-hidden text-left transition-all duration-300 cursor-pointer"
                    >
                        {/* Image Preview */}
                        {cert.image_url && (
                            <div className="relative aspect-[16/10] overflow-hidden bg-dev-bg-medium">
                                <Image
                                    src={cert.image_url}
                                    alt={cert.name}
                                    fill
                                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-dev-bg-dark/60 to-transparent" />
                                {/* Zoom hint */}
                                <div className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-lg bg-black/40 text-white/70 backdrop-blur-sm opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                                    </svg>
                                </div>
                            </div>
                        )}

                        {/* Info */}
                        <div className="p-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xl">{cert.icon}</span>
                                <h3 className="font-semibold text-dev-text-primary transition-colors group-hover:text-dev-cyan">
                                    {cert.name}
                                </h3>
                            </div>
                            {cert.description && (
                                <p className="mt-1 text-sm text-dev-text-muted">{cert.description}</p>
                            )}
                            {cert.date && (
                                <p className="mt-2 font-mono text-xs text-dev-text-muted">{cert.date}</p>
                            )}
                        </div>
                    </motion.button>
                ))}

                {certificates.length === 0 && (
                    <div className="clay-card flex flex-col items-center justify-center p-6 text-center border-2 border-dashed border-white/10 min-h-[200px]">
                        <span className="text-3xl mb-2 opacity-40">📜</span>
                        <p className="text-sm text-dev-text-muted">No certificates yet</p>
                    </div>
                )}
            </div>

            {/* ═══════ Lightbox Modal ═══════ */}
            <AnimatePresence>
                {selected && selected.image_url && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                        onClick={() => setSelected(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.2, 0, 0, 1] as const }}
                            className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-dev-bg-medium shadow-2xl border border-white/10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button
                                onClick={() => setSelected(null)}
                                className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white/80 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"
                            >
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            {/* Image */}
                            <div className="relative aspect-[16/10] w-full">
                                <Image
                                    src={selected.image_url}
                                    alt={selected.name}
                                    fill
                                    className="object-contain bg-white/5"
                                    sizes="(max-width: 768px) 100vw, 768px"
                                />
                            </div>

                            {/* Info bar */}
                            <div className="flex items-center gap-3 border-t border-white/10 px-6 py-4">
                                <span className="text-2xl">{selected.icon}</span>
                                <div>
                                    <h3 className="font-semibold text-dev-text-primary">{selected.name}</h3>
                                    {selected.description && (
                                        <p className="text-sm text-dev-text-muted">{selected.description}</p>
                                    )}
                                </div>
                                {selected.date && (
                                    <span className="ml-auto font-mono text-xs text-dev-text-muted">{selected.date}</span>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
